import { BoxProps } from "@mui/material";
import { forwardRef } from "react";

export type FlexBaseDirection = "row" | "column" | "none";
/* 
  FlexContainerType:
  - "s_": justify-start
  - "e_": justify-end
  - "c_": justify-center
  - "b_": justify-between
  - "_s": item-start
  - "_e": item-end
  - "_c": item-center
*/
export type FlexContainerType =
  | "ss"
  | "es"
  | "cs"
  | "bs"
  | "se"
  | "ee"
  | "ce"
  | "be"
  | "sc"
  | "ec"
  | "cc"
  | "bc";

export type FlexConfig = {
  section?: string;
  fullWidth?: boolean;
  fullHeight?: boolean;
  direction?: FlexBaseDirection;
  justifyContent?: string;
  alignItems?: string;
  type?: FlexContainerType;
};

export type FlexBaseProps = FlexConfig & {
  children: React.ReactNode;
} & BoxProps;

const withFlexBase = <P extends object>(
  Component: React.ComponentType<P>,
  direction: FlexBaseDirection,
) => {
  return forwardRef<any, FlexConfig & P>(
    ({ section, fullWidth, fullHeight, type = "ss", ...props }, ref) => {
      const { sx, ...rest } = props as any;

      const [justify, align] = type?.split("") ?? ["", ""];
      let justifyContent;
      let alignItems;

      if (justify === "s") {
        justifyContent = "flex-start";
      } else if (justify === "e") {
        justifyContent = "flex-end";
      } else if (justify === "c") {
        justifyContent = "center";
      } else {
        justifyContent = "space-between";
      }

      if (align === "s") {
        alignItems = "flex-start";
      } else if (align === "e") {
        alignItems = "flex-end";
      } else if (align === "c") {
        alignItems = "center";
      }

      const flexStyles = {
        display: direction === "none" ? "block" : "flex",
        ...(direction !== "none" && { flexDirection: direction }),
        ...(alignItems && { alignItems }),
        ...(justifyContent && { justifyContent }),
        ...(fullHeight && { height: "100%" }),
        ...(fullWidth && { width: "100%" }),
        ...sx,
      };

      return <Component ref={ref} sx={flexStyles} {...rest} />;
    },
  );
};

export { withFlexBase };
